import { PerspectiveCamera, OrthographicCamera, Scene } from "three";

/**
 * @description 캔버스 크기로부터 화면 비율을 계산하는 함수
 * @param canvas - HTML 캔버스 엘리먼트
 * @param width - 선택적 너비
 * @param height - 선택적 높이
 * @returns 계산된 화면 비율
 */
export function calculateCanvasAspectRatio(
  canvas: HTMLCanvasElement,
  width?: number,
  height?: number
): number {
  if (width !== undefined && height !== undefined) {
    return width / height;
  }

  const canvasRect = canvas.getBoundingClientRect();
  const actualWidth = canvasRect.width || canvas.width;
  const actualHeight = canvasRect.height || canvas.height;

  return actualWidth / actualHeight;
}

/**
 * @description PerspectiveCamera 리사이즈 함수
 * @param camera - PerspectiveCamera 객체
 * @param aspectRatio - 새로운 화면 비율
 */
export function resizePerspectiveCamera(
  camera: PerspectiveCamera,
  aspectRatio: number
): void {
  camera.aspect = aspectRatio;
  camera.updateProjectionMatrix();
}

/**
 * @description OrthographicCamera 리사이즈 함수
 * @param camera - OrthographicCamera 객체
 * @param aspectRatio - 새로운 화면 비율
 * @param frustumSize - 절두체 크기
 */
export function resizeOrthographicCamera(
  camera: OrthographicCamera,
  aspectRatio: number,
  frustumSize: number = 10
): void {
  camera.left = -frustumSize * aspectRatio / 2;
  camera.right = frustumSize * aspectRatio / 2;
  camera.top = frustumSize / 2;
  camera.bottom = -frustumSize / 2;
  camera.updateProjectionMatrix();
}

/**
 * @description 카메라 리사이즈 함수 (타입에 따라 자동 분기)
 * @param camera - 카메라 객체
 * @param aspectRatio - 새로운 화면 비율
 * @param frustumSize - OrthographicCamera의 절두체 크기
 */
export function resizeCameraFromType(
  camera: PerspectiveCamera | OrthographicCamera,
  aspectRatio: number,
  frustumSize: number = 10
): void {
  if (camera instanceof PerspectiveCamera) {
    resizePerspectiveCamera(camera, aspectRatio);
  } else if (camera instanceof OrthographicCamera) {
    resizeOrthographicCamera(camera, aspectRatio, frustumSize);
  }
}

/**
 * @description 카메라 맵의 모든 카메라 리사이즈 함수
 * @param cameras - 카메라 맵
 * @param aspectRatio - 새로운 화면 비율
 * @param frustumSize - OrthographicCamera의 절두체 크기
 */
export function resizeAllCameras(
  cameras: Map<string, PerspectiveCamera | OrthographicCamera>,
  aspectRatio: number,
  frustumSize: number = 10
): void {
  cameras.forEach(camera => {
    resizeCameraFromType(camera, aspectRatio, frustumSize);
  });
}

/**
 * @description 카메라를 씬에 추가하는 함수
 * @param camera - 카메라 객체
 * @param scene - 씬 객체
 */
export function addCameraToScene(
  camera: PerspectiveCamera | OrthographicCamera,
  scene: Scene
): void {
  scene.add(camera);
}

/**
 * @description 카메라를 씬에서 제거하는 함수
 * @param camera - 카메라 객체
 * @param scene - 씬 객체
 */
export function removeCameraFromScene(
  camera: PerspectiveCamera | OrthographicCamera,
  scene: Scene
): void {
  scene.remove(camera);
}

/**
 * @description 카메라 맵의 모든 카메라를 씬에 추가하는 함수
 * @param cameras - 카메라 맵
 * @param scene - 씬 객체
 */
export function addAllCamerasToScene(
  cameras: Map<string, PerspectiveCamera | OrthographicCamera>,
  scene: Scene
): void {
  cameras.forEach(camera => {
    addCameraToScene(camera, scene);
  });
}

/**
 * @description 카메라 정리 함수
 * @param camera - 카메라 객체
 * @param scene - 씬 객체
 */
export function cleanupCamera(
  camera: PerspectiveCamera | OrthographicCamera,
  scene: Scene
): void {
  // 씬에서 제거
  removeCameraFromScene(camera, scene);

  // 카메라 리소스 정리 (필요한 경우)
  // Three.js 카메라는 특별히 dispose 할 리소스가 없지만, 
  // 미래에 필요할 수 있으므로 구조를 유지
}

/**
 * @description 카메라 맵의 모든 카메라를 정리하는 함수
 * @param cameras - 카메라 맵
 * @param scene - 씬 객체
 */
export function cleanupAllCameras(
  cameras: Map<string, PerspectiveCamera | OrthographicCamera>,
  scene: Scene
): void {
  cameras.forEach(camera => {
    cleanupCamera(camera, scene);
  });
  cameras.clear();
}
